
import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import axiosApi from "../../Utils/axiosApi";
import SingleBill from "../../GuestManage/SingleBill";

const NewStayDialog = ({
  open,
  onClose,
  stayId,
  hostelId,
  occupancyEditType = null,
}) => {
  const [stayDetails, setStayDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(
  occupancyEditType ? "edit" : "view"
);

  const isExistingStay = stayId != null && stayId !== "";
useEffect(() => {
  if (!open) return;

  setMode(
    isExistingStay
      ? occupancyEditType
        ? "edit"
        : "view"
      : "create"
  );
}, [open, stayId, isExistingStay, occupancyEditType]);
  
  const fetchStayDetails = useCallback(
    async (signal) => {
      if (!open || !isExistingStay) {
        setStayDetails(null);
        setLoading(false);
        setError("");
        return;
      }

      setLoading(true);
      setError("");
      setStayDetails(null);

      try {
        const response = await axiosApi.post(
          "/stayServiceBooking/getStayServiceBooking",
          {
            hostelId: hostelId || null,
            stayServiceBookingIds: [stayId],
          }
        );

        if (signal.cancelled) return;

        const result = response.data?.data ?? response.data;

        const records = Array.isArray(result)
          ? result
          : Array.isArray(result?.shortStayMeals)
          ? result.shortStayMeals
          : Array.isArray(result?.data)
          ? result.data
          : null;

        const stay = records
          ? records.find(
              (item) =>
                String(
                  item.id ??
                    item.stayId ??
                    item.stayServiceBookingId
                ) === String(stayId)
            ) ?? records[0] ?? null
          : result;

        if (!stay || typeof stay !== "object") {
          setError("No stay details were found for this booking.");
          return;
        }

        setStayDetails(stay);
      } catch (err) {
        if (signal.cancelled) return;

        console.error("Failed to fetch stay details:", err);

        setError(
          err.response?.data?.message ||
            "Unable to load stay details. Please try again."
        );
      } finally {
        if (!signal.cancelled) {
          setLoading(false);
        }
      }
    },
    [open, isExistingStay, stayId, hostelId]
  );

  useEffect(() => {
    const signal = { cancelled: false };

    fetchStayDetails(signal);

    return () => {
      signal.cancelled = true;
    };
  }, [fetchStayDetails]);

  const handleClose = () => {
    setStayDetails(null);
    setError("");
    onClose?.();
  };

  const handleRetry = () => {
    const signal = { cancelled: false };
    fetchStayDetails(signal);
  };

  const handleEdit = () => {
    setMode("edit");
  };

  const handleSaved = () => {
    // Optionally refetch the saved record here if SingleBill
    // reports successful saving through a callback.
    setMode("view");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "95vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 0.75,
          px: 1.5,
          fontSize: "1rem",
          fontWeight: 600,
        }}
      >
        {mode === "create"
          ? "New Stay"
          : mode === "edit"
          ? "Edit Stay"
          : "Stay Details"}

        <IconButton
          size="small"
          color="error"
          onClick={handleClose}
          aria-label="Close stay dialog"
          sx={{
            border: "1px solid #fecaca",
            borderRadius: 1.5,
            width: 28,
            height: 28,
            p: 0,
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 1, sm: 1.5 },
          overflowY: "auto",
          overflowX: "hidden",
          minWidth: 0,
        }}
      >
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              py: 5,
            }}
          >
            <CircularProgress size={24} />
            Loading stay details...
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>

            <Button
              variant="contained"
              onClick={handleRetry}
            >
              Retry
            </Button>
          </Box>
        )}

        {!loading && !error && (
         <SingleBill
  key={isExistingStay ? String(stayId) : "new-stay"}
  showHeader={false}
  compact
  initialGuestDetails={
    isExistingStay ? stayDetails : null
  }
  mode={mode}
  occupancyEditType={occupancyEditType}
  onEdit={handleEdit}
  onSaved={handleSaved}
/>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewStayDialog;
